import {useState, useCallback, useRef} from 'react';
import * as api from '../services/fissionBoard';
import {LeaderboardItemType} from '@/common-pages/leaderboard/types';
import {getYesterdayReward} from '@/common-pages/leaderboard/services/fissionBoard';
import {useYesterdayRewardModal} from './useYesterdayRewardModal';
import {useFocusEffect} from '@react-navigation/native';

export function useFissionBoard() {
  const [rankingList, setRankingList] = useState<LeaderboardItemType[]>([]);
  const [currentCycle, setCurrentCycle] = useState<number>();
  const [jackpot, setJackpot] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [cycleEndDate, setCycleEndDate] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  // 使用共享的昨日奖励弹窗逻辑
  useYesterdayRewardModal(getYesterdayReward, 'FISSION_RANK_ACTIVITY');

  const [myRanking, setMyRanking] = useState<api.GetMyRankingResponse | null>(
    null,
  );
  const [myRankingLoading, setMyRankingLoading] = useState(true);

  // 当前请求的期号，默认为空表示获取最新期
  const currentCycleNum = useRef<number | null>(null);
  // 保存从接口获取的最新期号，使用 ref 避免依赖循环
  const latestCycleNumRef = useRef<number | null>(null);

  const loadLeaderboard = useCallback(
    async (cycleNum?: number | null, skipLoading = false) => {
      if (!skipLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const params = typeof cycleNum === 'number' ? {cycleNum} : {};
        const result = await api.getLeaderboard(params);

        if (result) {
          const transformedRankingList: LeaderboardItemType[] =
            result.rankingList.map(item => ({
              id: item.fissionUserId.toString(),
              username: item.userName,
              avatar: item.userAvatar,
              rank: item.ranking,
              bonus: item.bonusAmount,
              point: item.inviteTotalCount,
              userPhone: '',
              rewardRate: 0,
              rewardRateStr: '0%',
            }));
          // 如果列表长度不足20个，补齐默认值
          const paddedList = [...transformedRankingList];
          while (paddedList.length < 20) {
            paddedList.push({
              id: `default-${paddedList.length + 1}`,
              username: '',
              avatar: '',
              rank: paddedList.length + 1,
              bonus: 0,
              point: 0,
              userPhone: '',
              rewardRate: 0,
              rewardRateStr: '0%',
            });
          }
          setRankingList(paddedList);
          setCurrentCycle(result.currentCycle);
          setJackpot(result.jackpot);
          setCycleEndDate(result.cycleEndDate);

          // 只在第一次获取数据时设置最新期号
          if (
            latestCycleNumRef.current === null ||
            latestCycleNumRef.current < result.currentCycle
          ) {
            latestCycleNumRef.current = result.currentCycle;
          }
          currentCycleNum.current = result.currentCycle;

          return result; // 返回结果供外部使用
        }
      } catch (err) {
        setError('获取排行榜数据失败');
      } finally {
        if (!skipLoading) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const loadMyRanking = useCallback(
    async (cycleNum?: number | null, skipLoading = false) => {
      if (!skipLoading) {
        setMyRankingLoading(true);
      }

      try {
        const params = cycleNum ? {cycleNum} : {};
        const result = await api.getMyRanking(params);
        setMyRanking(result);
      } catch (err) {
        setMyRanking(null);
      } finally {
        if (!skipLoading) {
          setMyRankingLoading(false);
        }
      }
    },
    [],
  );
  const refresh = (cycleNum: number | null, skipLoading = false) => {
    loadLeaderboard(cycleNum, skipLoading);
    loadMyRanking(cycleNum, skipLoading);
  };
  const timer = useRef<NodeJS.Timeout>();
  const startInterval = () => {
    timer.current && clearInterval(timer.current);
    timer.current = setInterval(() => {
      refresh(currentCycleNum.current, true);
    }, 30 * 1000);
  };
  const stopInterval = () => {
    timer.current && clearInterval(timer.current);
    timer.current = void 0;
  };
  useFocusEffect(
    useCallback(() => {
      refresh(null);
      startInterval(); // 页面获得焦点时启动定时器
      return () => {
        stopInterval(); // 页面失去焦点时清理定时器
      };
      // eslint-disable-next-line
    }, []),
  );

  // 切换到上一期
  const switchToPrevious = useCallback(() => {
    if (latestCycleNumRef.current !== null) {
      let cycle = latestCycleNumRef.current - 1;
      if (cycle === 0) {
        cycle = -1;
      }
      refresh(cycle);
      startInterval();
    }
    // eslint-disable-next-line
  }, []);

  // 切换到当前期
  const switchToCurrent = useCallback(() => {
    refresh(latestCycleNumRef.current);
    startInterval();
    // eslint-disable-next-line
  }, []);

  return {
    rankingList,
    currentCycle,
    jackpot,
    loading,
    error,
    myRanking,
    myRankingLoading,
    refresh,
    // 期号切换相关
    switchToPrevious,
    switchToCurrent,
    cycleEndDate,
  };
}
